import React, { useMemo, useState } from 'react';
import { Search, Filter, X, ArrowUpDown, ArrowUp, ArrowDown, Building, MapPin, Users, Clock, Coins } from 'lucide-react';
import { RosterModal } from './RosterModal';
import { EmptyState } from './EmptyState';
interface Site {
  id: string;
  name: string;
  province: string;
  area: string;
  totalGuards: number;
  overtimeHours: number;
  projectedCost: number;
  status: 'Healthy' | 'Warning' | 'Critical';
}
const mockSites: Site[] = [{
  id: '1',
  name: 'Woolworths HQ',
  province: 'Western Cape',
  area: 'Cape Town CBD',
  totalGuards: 12,
  overtimeHours: 104,
  projectedCost: 12500,
  status: 'Critical'
}, {
  id: '2',
  name: 'Standard Bank Tower',
  province: 'Gauteng',
  area: 'Sandton',
  totalGuards: 18,
  overtimeHours: 76,
  projectedCost: 8200,
  status: 'Warning'
}, {
  id: '3',
  name: 'Pick n Pay Constantia',
  province: 'Western Cape',
  area: 'Southern Suburbs',
  totalGuards: 8,
  overtimeHours: 62,
  projectedCost: 6500,
  status: 'Warning'
}, {
  id: '4',
  name: 'Nedbank Plaza',
  province: 'Gauteng',
  area: 'Sandton',
  totalGuards: 15,
  overtimeHours: 58,
  projectedCost: 5800,
  status: 'Warning'
}, {
  id: '5',
  name: 'Checkers Claremont',
  province: 'Western Cape',
  area: 'Southern Suburbs',
  totalGuards: 10,
  overtimeHours: 54,
  projectedCost: 5200,
  status: 'Warning'
}, {
  id: '6',
  name: 'FNB Building',
  province: 'Gauteng',
  area: 'Midrand',
  totalGuards: 22,
  overtimeHours: 50,
  projectedCost: 4800,
  status: 'Warning'
}, {
  id: '7',
  name: 'Spar Distribution Centre',
  province: 'KwaZulu-Natal',
  area: 'Durban Central',
  totalGuards: 30,
  overtimeHours: 36,
  projectedCost: 3200,
  status: 'Healthy'
}, {
  id: '8',
  name: 'Dischem Warehouse',
  province: 'Gauteng',
  area: 'Midrand',
  totalGuards: 14,
  overtimeHours: 32,
  projectedCost: 2800,
  status: 'Healthy'
}, {
  id: '9',
  name: 'Shoprite Bellville',
  province: 'Western Cape',
  area: 'Northern Suburbs',
  totalGuards: 16,
  overtimeHours: 24,
  projectedCost: 2100,
  status: 'Healthy'
}, {
  id: '10',
  name: 'ABSA Centre',
  province: 'Western Cape',
  area: 'Cape Town CBD',
  totalGuards: 20,
  overtimeHours: 18,
  projectedCost: 1500,
  status: 'Healthy'
}, {
  id: '11',
  name: 'Discovery HQ',
  province: 'Gauteng',
  area: 'Sandton',
  totalGuards: 25,
  overtimeHours: 12,
  projectedCost: 1000,
  status: 'Healthy'
}, {
  id: '12',
  name: 'Old Mutual Tower',
  province: 'Western Cape',
  area: 'Cape Town CBD',
  totalGuards: 18,
  overtimeHours: 8,
  projectedCost: 600,
  status: 'Healthy'
}, {
  id: '13',
  name: 'V&A Waterfront',
  province: 'Western Cape',
  area: 'Cape Town CBD',
  totalGuards: 45,
  overtimeHours: 120,
  projectedCost: 15000,
  status: 'Critical'
}, {
  id: '14',
  name: 'Sandton City Mall',
  province: 'Gauteng',
  area: 'Sandton',
  totalGuards: 35,
  overtimeHours: 95,
  projectedCost: 11000,
  status: 'Critical'
}, {
  id: '15',
  name: 'OR Tambo Airport',
  province: 'Gauteng',
  area: 'Kempton Park',
  totalGuards: 60,
  overtimeHours: 150,
  projectedCost: 18000,
  status: 'Critical'
}];
type SortField = 'name' | 'province' | 'totalGuards' | 'overtimeHours' | 'projectedCost';
type SortDirection = 'asc' | 'desc';
export function AllSitesView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('All Provinces');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const clearFilters = () => {
    setSearchQuery('');
    setProvinceFilter('All Provinces');
    setStatusFilter('All Statuses');
  };
  const filteredAndSortedSites = useMemo(() => {
    let result = mockSites;
    // Filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.area.toLowerCase().includes(q));
    }
    if (provinceFilter !== 'All Provinces') {
      result = result.filter(s => s.province === provinceFilter);
    }
    if (statusFilter !== 'All Statuses') {
      result = result.filter(s => s.status === statusFilter);
    }
    // Sort
    result = [...result].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [searchQuery, provinceFilter, statusFilter, sortField, sortDirection]);
  const isFiltered = searchQuery || provinceFilter !== 'All Provinces' || statusFilter !== 'All Statuses';
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Healthy':
        return 'bg-emerald-100 text-emerald-700';
      case 'Warning':
        return 'bg-amber-100 text-amber-700';
      case 'Critical':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-[#e5e5e5]" data-id="element-353" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-[#062133]" data-id="element-354" /> : <ArrowDown className="w-3 h-3 ml-1 text-[#062133]" data-id="element-355" />;
  };
  return <div className="flex flex-col space-y-6 animate-in fade-in duration-300" data-id="element-356">
      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-[#e5e5e5] shadow-sm flex flex-col md:flex-row gap-4" data-id="element-357">
        <div className="relative flex-1" data-id="element-358">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5a7280]" data-id="element-359" />
          <input type="text" placeholder="Search sites or areas..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent transition-colors" data-id="element-360" />
        </div>

        <div className="flex flex-col md:flex-row gap-4" data-id="element-361">
          <select value={provinceFilter} onChange={e => setProvinceFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm text-[#062133] focus:outline-none focus:ring-2 focus:ring-[#ff697f] min-w-[160px]" data-id="element-362">
            <option data-id="element-363">All Provinces</option>
            <option data-id="element-364">Western Cape</option>
            <option data-id="element-365">Gauteng</option>
            <option data-id="element-366">KwaZulu-Natal</option>
          </select>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#e5e5e5] rounded-xl text-sm text-[#062133] focus:outline-none focus:ring-2 focus:ring-[#ff697f] min-w-[160px]" data-id="element-367">
            <option data-id="element-368">All Statuses</option>
            <option data-id="element-369">Healthy</option>
            <option data-id="element-370">Warning</option>
            <option data-id="element-371">Critical</option>
          </select>

          {isFiltered && <button onClick={clearFilters} className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-xl transition-colors" data-id="element-372">
              <X className="h-4 w-4" data-id="element-373" />
              Clear
            </button>}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl border border-[#e5e5e5] shadow-sm overflow-hidden" data-id="element-374">
        <table className="w-full text-left border-collapse" data-id="element-375">
          <thead data-id="element-376">
            <tr className="bg-[#fafaf9] border-b border-[#e5e5e5]" data-id="element-377">
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('name')} data-id="element-378">
                <div className="flex items-center" data-id="element-379">
                  Site <SortIcon field="name" data-id="element-380" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('province')} data-id="element-381">
                <div className="flex items-center" data-id="element-382">
                  Location <SortIcon field="province" data-id="element-383" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('totalGuards')} data-id="element-384">
                <div className="flex items-center" data-id="element-385">
                  Guards <SortIcon field="totalGuards" data-id="element-386" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('overtimeHours')} data-id="element-387">
                <div className="flex items-center" data-id="element-388">
                  Overtime <SortIcon field="overtimeHours" data-id="element-389" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('projectedCost')} data-id="element-390">
                <div className="flex items-center" data-id="element-391">
                  Cost <SortIcon field="projectedCost" data-id="element-392" />
                </div>
              </th>
              <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase" data-id="element-393">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5]" data-id="element-394">
            {filteredAndSortedSites.length > 0 ? filteredAndSortedSites.map(site => <tr key={site.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => setSelectedSite(site)} data-id="element-395">
                  <td className="py-4 px-6" data-id="element-396">
                    <div className="flex items-center gap-3" data-id="element-397">
                      <div className="h-8 w-8 rounded-full bg-[#062133]/5 flex items-center justify-center flex-shrink-0" data-id="element-398">
                        <Building className="h-4 w-4 text-[#062133]" data-id="element-399" />
                      </div>
                      <span className="text-sm font-semibold text-[#062133]" data-id="element-400">
                        {site.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-401">
                    <p className="text-sm text-[#062133]" data-id="element-402">{site.province}</p>
                    <p className="text-xs text-[#5a7280] flex items-center gap-1 mt-0.5" data-id="element-403">
                      <MapPin className="w-3 h-3" data-id="element-404" />
                      {site.area}
                    </p>
                  </td>
                  <td className="py-4 px-6" data-id="element-405">
                    <div className="flex items-center text-sm text-[#5a7280]" data-id="element-406">
                      <Users className="w-4 h-4 mr-1.5" data-id="element-407" /> {site.totalGuards}
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-408">
                    <div className="flex items-center text-sm text-[#5a7280]" data-id="element-409">
                      <Clock className="w-4 h-4 mr-1.5" data-id="element-410" /> {site.overtimeHours}h
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-411">
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-412">
                      <Coins className="w-4 h-4 mr-1.5 text-[#5a7280]" data-id="element-413" /> R{' '}
                      {site.projectedCost.toLocaleString()}
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-414">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(site.status)}`} data-id="element-415">
                      {site.status}
                    </span>
                  </td>
                </tr>) : <tr data-id="element-416">
                <td colSpan={6} className="p-0" data-id="element-417">
                  <EmptyState icon={Building} title="No sites found" description="Try adjusting your search or filters" data-id="element-418" />
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4" data-id="element-419">
        {filteredAndSortedSites.length > 0 ? filteredAndSortedSites.map(site => <div key={site.id} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 cursor-pointer hover:border-[#5a7280]/30 transition-colors" onClick={() => setSelectedSite(site)} data-id="element-420">
              <div className="flex justify-between items-start" data-id="element-421">
                <div className="flex items-center gap-3" data-id="element-422">
                  <div className="h-10 w-10 rounded-full bg-[#062133]/5 flex items-center justify-center flex-shrink-0" data-id="element-423">
                    <Building className="h-5 w-5 text-[#062133]" data-id="element-424" />
                  </div>
                  <div data-id="element-425">
                    <h3 className="text-base font-semibold text-[#062133]" data-id="element-426">
                      {site.name}
                    </h3>
                    <p className="text-xs text-[#5a7280] flex items-center gap-1 mt-0.5" data-id="element-427">
                      <MapPin className="w-3 h-3" data-id="element-428" />
                      {site.area}, {site.province}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 py-3 border-t border-b border-[#e5e5e5]" data-id="element-429">
                <div data-id="element-430">
                  <p className="text-[10px] text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-431">
                    Guards
                  </p>
                  <p className="text-sm font-medium text-[#062133]" data-id="element-432">
                    {site.totalGuards}
                  </p>
                </div>
                <div data-id="element-433">
                  <p className="text-[10px] text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-434">
                    Overtime
                  </p>
                  <p className="text-sm font-medium text-[#062133]" data-id="element-435">
                    {site.overtimeHours}h
                  </p>
                </div>
                <div data-id="element-436">
                  <p className="text-[10px] text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-437">
                    Cost
                  </p>
                  <p className="text-sm font-medium text-[#062133]" data-id="element-438">
                    R {site.projectedCost.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-center" data-id="element-439">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(site.status)}`} data-id="element-440">
                  {site.status}
                </span>
                <span className="text-xs font-medium text-[#ff697f]" data-id="element-441">
                  View Roster →
                </span>
              </div>
            </div>) : <EmptyState icon={Building} title="No sites found" description="Try adjusting your search or filters" data-id="element-442" />}
      </div>

      {/* Roster Modal */}
      <RosterModal isOpen={selectedSite !== null} siteName={selectedSite?.name || ''} siteId={selectedSite?.id || ''} onClose={() => setSelectedSite(null)} onTakeAction={() => setSelectedSite(null)} data-id="element-443" />
    </div>;
}